export const VERIFICATION_FEEDBACK_KEY = 'wolhasudang.verification-feedback.v1';
const PLAYED_MONTH_PREFIX = 'wolhasudang.verification-feedback.played:';

export type VerificationFeedbackPreference = { enabled: boolean; quietStartHour: number; quietEndHour: number };
export type VerificationFeedbackVerdict = 'match' | 'difference';
export type VerificationHaptic = (verdict: VerificationFeedbackVerdict) => Promise<'played' | 'unsupported'>;

const DEFAULT_PREFERENCE: VerificationFeedbackPreference = { enabled: true, quietStartHour: 22, quietEndHour: 7 };

function validHour(value: unknown, fallback: number): number {
  return Number.isInteger(value) && Number(value) >= 0 && Number(value) <= 23 ? Number(value) : fallback;
}

export function readVerificationFeedbackPreference(raw: string | null): VerificationFeedbackPreference {
  if (!raw) return { ...DEFAULT_PREFERENCE };
  try {
    const value = JSON.parse(raw) as Partial<VerificationFeedbackPreference>;
    return {
      enabled: value.enabled !== false,
      quietStartHour: validHour(value.quietStartHour, DEFAULT_PREFERENCE.quietStartHour),
      quietEndHour: validHour(value.quietEndHour, DEFAULT_PREFERENCE.quietEndHour),
    };
  } catch {
    return { ...DEFAULT_PREFERENCE };
  }
}

export function isQuietHour(hour: number, preference: VerificationFeedbackPreference): boolean {
  if (preference.quietStartHour === preference.quietEndHour) return false;
  return preference.quietStartHour < preference.quietEndHour
    ? hour >= preference.quietStartHour && hour < preference.quietEndHour
    : hour >= preference.quietStartHour || hour < preference.quietEndHour;
}

export function canPlayVerificationFeedback(
  month: string,
  preference: VerificationFeedbackPreference,
  storage: Pick<Storage, 'getItem'>,
  hour: number,
): boolean {
  return preference.enabled && !isQuietHour(hour, preference) && !storage.getItem(`${PLAYED_MONTH_PREFIX}${month}`);
}

export function feedbackPatternFor(verdict: VerificationFeedbackVerdict): { startFrequency: number; endFrequency: number; duration: number; haptic: VerificationFeedbackVerdict } {
  return verdict === 'match'
    ? { startFrequency: 392, endFrequency: 440, duration: 0.22, haptic: 'match' }
    : { startFrequency: 330, endFrequency: 294, duration: 0.16, haptic: 'difference' };
}

export async function playVerificationFeedback(
  month: string,
  verdict: VerificationFeedbackVerdict,
  preference: VerificationFeedbackPreference,
  haptic?: VerificationHaptic,
): Promise<'played' | 'quiet' | 'off' | 'already_played'> {
  if (typeof window === 'undefined') return 'off';
  if (!preference.enabled) return 'off';
  if (isQuietHour(new Date().getHours(), preference)) return 'quiet';
  if (window.localStorage.getItem(`${PLAYED_MONTH_PREFIX}${month}`)) return 'already_played';
  const pattern = feedbackPatternFor(verdict);
  let played = false;
  if (haptic) {
    try { played = await haptic(pattern.haptic) === 'played'; } catch { /* visual result remains the fallback */ }
  }
  const AudioContext = window.AudioContext;
  if (AudioContext) try {
      const context = new AudioContext();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(pattern.startFrequency, context.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(pattern.endFrequency, context.currentTime + pattern.duration * 0.72);
      gain.gain.setValueAtTime(0.025, context.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + pattern.duration);
      oscillator.connect(gain).connect(context.destination);
      oscillator.addEventListener('ended', () => { void context.close(); }, { once: true });
      oscillator.start();
      oscillator.stop(context.currentTime + pattern.duration);
      played = true;
    } catch { /* haptic or visual result remains the fallback */ }
  if (played) {
    window.localStorage.setItem(`${PLAYED_MONTH_PREFIX}${month}`, '1');
    return 'played';
  }
  return 'off';
}
